/* eslint-disable no-console */

import {
  PAGE_SET_MANY,
  PAGE_UPDATE_MANY,
  SET_PAGE_SUBPAGE_LIST,
  CREATE_PAGE_SUCCESS,
  PAGE_DATA_FETCH_START,
  PAGE_DATA_FETCH_SUCCESS,
  PAGE_DATA_FETCH_FAILED,
} from './actions';
import {
  SET_PAGE_FAVOURITED,
  SET_PAGE_SUBSCRIBED_ACTION_TYPES,
} from '../BlockPageUI/actions';

import RESOURCE from '../../resource/Resource';
import { PAGE as PAGE_RESOURCE } from '../resourceNames';
import { SET_BLOCK_PROGRESS, UPDATE_BLOCK } from '../Blocks/actions';
import { updateProgressSettings } from '../Blocks/dispatchers';
import { refreshCourseProgressData } from '../Courses/dispatchers';
import { updatePageProgress } from '../PageProgress/dispatchers';
import { makeGetHasUserCommentedSelector } from '../PageProgress/selectors';
import { pagesByPath } from './selectors';
import { makeMetricsLogEvent } from '../Metrics/manualEventDispatchers';
import { selectCurrentCohortPath } from '../Cohorts/CurrentCohortPath/selectors';
import {
  DELETE_PAGE_FAILURE,
  DELETE_PAGE_START,
  DELETE_PAGE_SUCCESS,
} from '../PageSettings/actions';

// Note: This will remove block data on blockpages if used incorrectly
export const setPages = (pages) => ({
  type: PAGE_SET_MANY,
  pages,
});

export const updatePages = (pages, changes) => ({
  type: PAGE_UPDATE_MANY,
  pages,
  changes,
});

/**
 * Creates a page, updates the store and calls callback upon success/fail.
 *
 * callback(success, errorMessage, pageData)
 */
export const createPage = (basePath, data, callback) => {
  // TODO: tags = [], permissions = null
  return (dispatch) => {
    const cleanTitle = data.title.trim();
    let titleSlug = cleanTitle
      .toLowerCase()
      .replace(/[^a-z0-9]/g, ' ')
      .trim()
      .replace(/ +/g, '-');

    if (titleSlug.length === 0) {
      titleSlug = '_use_title';
    }

    const params = {
      path: `${basePath}/${titleSlug}`,
      title: cleanTitle,
      permissions: data.permissions,
      //     tags  = creationData.get('tags', []),
      //     permissions = json.loads(creationData.get('permissions', 'null')),
      //     coursePath = creationData.get('coursePath', None),
    };

    RESOURCE.create(PAGE_RESOURCE, params)
      .done((response, pageId) => {
        dispatch(makeMetricsLogEvent('page-create', { page: pageId }));

        // Create-multi call with ['data'] throws MethodNotImplemented
        // so gotta do this second request for the data.
        // 'data' is a property, not a function
        const readPageData = (pagePath) => {
          RESOURCE.read(PAGE_RESOURCE, pagePath, 'data')
            .done((pageData) => {
              dispatch({
                type: CREATE_PAGE_SUCCESS,
                pages: [pageData],
              });
              callback(true, null, pageData);
            })
            .fail(() => {
              readPageData(pagePath);
            });
        };

        readPageData(params.path);
      })
      .fail((error, response) => {
        callback(false, response.reason);
      });
  };
};

export function likePage(pagePath) {
  return (dispatch) => {
    dispatch(
      makeMetricsLogEvent('UpVote-Made', {
        onObject: 'Page',
        page: pagePath,
      })
    );
    return RESOURCE.update(PAGE_RESOURCE, pagePath, 'addFavourite').then(
      (result) => {
        const { numFavourites, hasFavourited } = result;
        dispatch(updatePages([pagePath], { numFavourites }));
        dispatch({
          type: SET_PAGE_FAVOURITED,
          pagePath,
          isFavourited: hasFavourited,
        });
      }
    );
  };
}

export function unlikePage(pagePath) {
  return (dispatch) => {
    dispatch(
      makeMetricsLogEvent('DownVote-Made', {
        onObject: 'Page',
        page: pagePath,
      })
    );
    return RESOURCE.update(PAGE_RESOURCE, pagePath, 'removeFavourite').then(
      (result) => {
        const { numFavourites, hasFavourited } = result;
        dispatch(updatePages([pagePath], { numFavourites }));
        dispatch({
          type: SET_PAGE_FAVOURITED,
          pagePath,
          isFavourited: hasFavourited,
        });
      }
    );
  };
}

export function updatePageSubscription(pagePath, action) {
  return (dispatch) => {
    return RESOURCE.update(PAGE_RESOURCE, pagePath, action, {
      topics: ['tree', 'page-edit'], // TODO: remove this after resource method is updated
    }).then(() => {
      // TODO: Possibly delay this call
      // Subscriptions are updated asynchronously so there's a chance that
      // the subscribe/unsubscribe action may not have completed yet (?)
      RESOURCE.read(PAGE_RESOURCE, pagePath, 'getSubscribedActionTypes').then(
        (subscribedActionTypes) => {
          dispatch({
            type: SET_PAGE_SUBSCRIBED_ACTION_TYPES,
            pagePath,
            subscribedActionTypes,
          });
        }
      );
    });
  };
}

export function subscribePage(pagePath) {
  return updatePageSubscription(pagePath, 'subscribe');
}

export function unsubscribePage(pagePath) {
  return updatePageSubscription(pagePath, 'unsubscribe');
}

export function updateBlockProgressSettings(
  pagePath,
  blockId,
  progressSettings
) {
  return (dispatch) => {
    return RESOURCE.update(
      PAGE_RESOURCE,
      pagePath,
      'updateBlockProgressSettings',
      {
        blockID: blockId,
        // Twig: why do we need to stringify again?
        progressSettings: JSON.stringify(progressSettings),
      }
    ).then((result) => {
      dispatch(updateProgressSettings(blockId, progressSettings));
      dispatch(refreshCourseProgressData);
      return result;
    });
  };
}

export function duplicateBlock(pagePath, blockId) {
  return (dispatch) => {
    return new Promise((resolve, reject) => {
      RESOURCE.update(PAGE_RESOURCE, pagePath, 'duplicateBlock', {
        blockId,
      }).then(resolve, reject);
    }).then((result) => {
      dispatch(refreshCourseProgressData);
      return result;
    });
  };
}

export function deletePage(pagePath) {
  return async (dispatch) => {
    dispatch({
      type: DELETE_PAGE_START,
      pagePath,
    });
    try {
      await RESOURCE.delete(PAGE_RESOURCE, pagePath);

      dispatch({
        type: DELETE_PAGE_SUCCESS,
        pagePath,
      });
    } catch (error) {
      dispatch({
        type: DELETE_PAGE_FAILURE,
        pagePath,
        error,
      });
    }
  };
}

export function deleteBlock(pagePath, blockId) {
  return (dispatch) => {
    return RESOURCE.update('page', pagePath, 'deleteBlock', {
      blockID: blockId,
    }).then((result) => {
      dispatch(refreshCourseProgressData);
      return result;
    });
  };
}

export function createBlock(pagePath, params) {
  return (dispatch) => {
    return new Promise((resolve, reject) => {
      RESOURCE.update(PAGE_RESOURCE, pagePath, 'createBlock', params).then(
        resolve,
        reject
      );
    }).then((result) => {
      dispatch(refreshCourseProgressData);
      return result;
    });
  };
}

export function fetchPageAttributesData(pagePath) {
  return () => {
    return RESOURCE.read(PAGE_RESOURCE, pagePath, [
      'toJSON',
      'getJSONSchema',
    ]).then(([json, schema]) => {
      return { json, schema };
    });
  };
}

export function updatePageAttributesFromJson(pagePath, json) {
  return () => {
    return RESOURCE.update(PAGE_RESOURCE, pagePath, 'fromJSON', {
      json,
    });
  };
}

export function fetchHasUserCommented(pagePath) {
  return (dispatch) => {
    return RESOURCE.read(
      PAGE_RESOURCE,
      pagePath,
      'getHasUserCommented'
    ).then((hasUserCommented) =>
      dispatch(updatePageProgress(pagePath, { hasUserCommented }))
    );
  };
}

export function refreshPageCommentProgress(pagePath, isCommentRequired) {
  return (dispatch, getState) => {
    const state = getState();
    const hasUserCommented = makeGetHasUserCommentedSelector(pagePath)(state);
    if (isCommentRequired && !hasUserCommented) {
      // it's possible the user has commented on the page already
      // fetch hasUserCommented so we can update the page progress
      dispatch(fetchHasUserCommented(pagePath));

      const page = pagesByPath(state).get(pagePath);
      if (page && page.get('isProgressContributor', false)) {
        // the course progress bar doesn't yet use the PageProgress store
        // so we have to refresh the module progress data so that it gets
        // fresh 'hasUserCommented' values
        dispatch(refreshCourseProgressData);
      }
    }
  };
}

export const fetchSubpages = (basePagePath, ignore) => (dispatch) => {
  return new Promise((resolve, reject) => {
    const params = {};
    if (ignore) {
      params.ignore = ignore;
    }
    RESOURCE.read('page', basePagePath, 'getSubpages', params)
      .done((results) => {
        results.forEach((info) => {
          dispatch(updatePages([info.path], info));
        });

        dispatch({
          type: SET_PAGE_SUBPAGE_LIST,
          pagePath: basePagePath,
          subpages: results.map((info) => info.id),
        });

        resolve();
      })
      .fail(reject);
  });
};

export const fetchImmediateSubpages = (
  basePagePath,
  includeProgress = true
) => (dispatch) => {
  return new Promise((resolve, reject) => {
    RESOURCE.read('page', basePagePath, 'getImmediateSubpages', {
      includeProgress,
    })
      .done((results) => {
        results.forEach((info) => {
          dispatch(updatePages([info.path], info));
        });

        dispatch({
          type: SET_PAGE_SUBPAGE_LIST,
          pagePath: basePagePath,
          subpages: results.map((info) => info.id),
        });

        resolve();
      })
      .fail(reject);
  });
};

export const fetchPage = (pagePath) => {
  return (dispatch) => {
    dispatch({
      type: PAGE_DATA_FETCH_START,
    });
    return RESOURCE.read('page', pagePath, 'data')
      .then((page) => {
        dispatch({
          type: PAGE_DATA_FETCH_SUCCESS,
          pages: [page],
        });
      })
      .catch((error) => {
        dispatch({ type: PAGE_DATA_FETCH_FAILED, error });
      });
  };
};

export const getPageExtraInfo = (pagePath) => {
  return (dispatch, getState) => {
    const state = getState();
    const pages = pagesByPath(state);
    const page = pages.get(pagePath);
    const cohortPath = selectCurrentCohortPath(state);
    if (page) {
      const methods = [
        {
          method: 'isFavourited',
          args: null,
        },
        {
          method: 'getTraversalLinks',
          args: { cohortPath },
        },
      ];
      RESOURCE.read('page', pagePath, methods)
        .then(([isFavourited, traversalLinks]) => {
          dispatch(updatePages([pagePath], { isFavourited, traversalLinks }));
          // TODO:  clean up the getTraversalLinks API call so that it returns the page summaries in the first place
          if (traversalLinks.back) {
            dispatch(fetchPage(traversalLinks.back.path));
          }
          if (traversalLinks.next) {
            dispatch(fetchPage(traversalLinks.next.path));
          }
        })
        .catch((err) => {
          console.log('err', err);
        });
    }
  };
};

export const getBlockCompletion = (blockID, pagePath) => {
  return (dispatch) => {
    RESOURCE.read('page', pagePath, 'getBlockCompletion', {
      blockID,
    }).then((result) => {
      dispatch({
        type: UPDATE_BLOCK,
        blockID,
        pagePath,
        changes: result,
      });
    });
  };
};

export const updateBlockProgress = (pagePath, blockID, progress) => {
  return (dispatch) => {
    const params = {
      blockID,
      progress,
    };
    return RESOURCE.update(
      PAGE_RESOURCE,
      pagePath,
      'setBlockProgress',
      params
    ).then((result) => {
      dispatch({
        type: SET_BLOCK_PROGRESS,
        blockID,
        pagePath,
        value: result.completion,
      });
      dispatch(getBlockCompletion(blockID, pagePath));
    });
  };
};

export const updateBlockInteraction = (blockID, pagePath, interactionData) => {
  return (dispatch) => {
    const params = {
      blockID,
      interaction: JSON.stringify(interactionData),
      group: null,
    };

    return RESOURCE.update(
      PAGE_RESOURCE,
      pagePath,
      'updateBlockInteraction',
      params
    ).then(() => {
      dispatch(getBlockCompletion(blockID, pagePath));
    });
  };
};

export const submitBlockPost = (blockID, pagePath, submission) => (
  dispatch
) => {
  const params = {
    blockID,
    submissionData: JSON.stringify(submission),
    submissionGroup: null,
  };

  return RESOURCE.update(
    PAGE_RESOURCE,
    pagePath,
    'submitUserData',
    params
  ).then(() => {
    dispatch(getBlockCompletion(blockID, pagePath));
  });
};

export const updateSubpageList = (pagePath, subpages) => (dispatch) => {
  const subpageIds = subpages.map((info) => info.id);
  dispatch({
    type: SET_PAGE_SUBPAGE_LIST,
    pagePath,
    subpages: subpageIds,
  });

  return RESOURCE.update(PAGE_RESOURCE, pagePath, 'updateSubpagesOrder', {
    subpages: subpageIds,
  });

  // TODO: dispatch SUCCESS/FAIL action
};
